import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import addressApi, { QUERY_GET_PROPERTIES } from "api/landlord/address";
import { Property } from "types/landlord/address";
import { Select, Option } from "shared/components/forms/Select";

export const PropertySearchSelect = ({
  value,
  onChange,
  id,
  placeholder = "Select property",
  className,
  selectedLabel,
  excludePropertyIds = []
}: {
  value: string;
  onChange: (propertyId: string) => void;
  id: string;
  placeholder?: string;
  className?: string;
  selectedLabel?: string | null;
  /** Property IDs to omit from the list (e.g. already selected in other rows). Current value is always kept. */
  excludePropertyIds?: string[];
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  const { data: propertiesData, isLoading: isLoadingProperties } = useQuery({
    queryKey: [QUERY_GET_PROPERTIES, debouncedSearchQuery || "", 1],
    queryFn: () =>
      addressApi.getProperties({
        searchTerm: debouncedSearchQuery || "",
        page: 1,
        per_page: 10
      })
  });

  const items = (propertiesData as { items?: Property[] } | undefined)?.items;
  const options: Option[] = useMemo(() => {
    const loaded =
      items?.map((p) => ({
        label: p.name || p.address.one_line_address || `Property ${p.id}`,
        value: String(p.id),
        oneLineAddress: p.address.one_line_address
      })) || [];
    const selectedId = String(value) || null;
    const excludeSet = new Set(excludePropertyIds);
    const available = loaded.filter(
      (o) => o.value === selectedId || !excludeSet.has(o.value)
    );
    const hasSelectedInList = available.some((o) => o.value === selectedId);
    if (selectedId && !hasSelectedInList) {
      const label = selectedLabel ?? `Property ${selectedId}`;
      return [...available, { label, value: selectedId }].sort((a, b) =>
        (a.label as string).localeCompare(b.label as string)
      );
    }
    return [...available].sort((a, b) =>
      (a.label as string).localeCompare(b.label as string)
    );
  }, [items, value, selectedLabel, excludePropertyIds]);

  return (
    <Select
      id={id}
      placeholder={placeholder}
      value={String(value) || undefined}
      onChange={(e) => onChange(e.target.value)}
      options={options}
      searchable
      disableFuzzyFiltering
      onInputChange={(inputValue) => setSearchQuery(inputValue)}
      loading={isLoadingProperties}
      clearable
      className={className}
      required
      menuPlacement="auto"
      menuPosition="fixed"
      formatOptionLabel={(option, { context }) => {
        const oneLineAddress = (option as { oneLineAddress?: string })
          .oneLineAddress;
        if (!oneLineAddress || context === "value")
          return (
            <span className="block text-base text-content-1000">
              {option.label}
            </span>
          );
        return (
          <span className="block">
            <span className="block text-base text-content-1000">
              {option.label}
            </span>
            <span className="block text-sm text-content-500">
              {oneLineAddress}
            </span>
          </span>
        );
      }}
    />
  );
};
